import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fieldKey } from '../../fieldKeys';
import { fieldOptionLabel, uiLang, usedFields } from '../../kinds';
import { Badge, Button, Dialog, Pill, TextField, Textarea } from '../../ui';

/**
 * The fields of the collection's item types, read-only (they were an
 * accordion above the list). Changing them is the item type's business:
 * the owner gets a link to Settings → Item types.
 */
export function FieldDefinitionsDialog({ inventory, canManage, onClose }) {
  const { t, i18n } = useTranslation();
  const types = (inventory.item_types && inventory.item_types.length > 0)
    ? inventory.item_types
    : [{ id: 'default', name: inventory.inventory_type_name || '', fields: inventory.fields || [] }];
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('collection.fields.title')}
      description={t('collection.fields.intro')}
      size="lg"
      footer={(
        <>
          {canManage && (
            <Button as={Link} to="/settings/item-types" variant="secondary" onClick={onClose}>{t('collection.fields.toSettings')}</Button>
          )}
          <Button onClick={onClose}>{t('common.close')}</Button>
        </>
      )}
    >
      <div className="field-defs">
        {types.map((type) => {
          const fields = usedFields(type.fields);
          return (
            <section key={type.id} className="field-defs__type" aria-label={type.name}>
              {types.length > 1 && (
                <h3 className="field-defs__heading">
                  {type.name}
                  {type.is_default && <Badge tone="accent">{t('collections.default')}</Badge>}
                </h3>
              )}
              {fields.length === 0 ? (
                <p className="ui-field__hint">{t('collection.noFields')}</p>
              ) : (
                <ul className="field-defs__list">
                  {fields.map((field) => (
                    <li key={fieldKey(field)} className="field-defs__row">
                      <div className="field-defs__name">
                        <strong>{field.name}</strong>
                        {field.required && <span className="field-defs__required"><span aria-hidden="true">*</span><span className="visually-hidden">{t('collection.fields.required')}</span></span>}
                        <span className="field-defs__type-name">{t(`fieldType.${field.type}`, { defaultValue: field.type })}</span>
                        {field.role && <Pill tone="accent">{t(`itemTypeForm.roleOption.${field.role}`, { defaultValue: field.role })}</Pill>}
                        <Pill tone={field.in_view !== false ? 'green' : 'amber'}>
                          {field.in_view !== false ? t('collection.visible') : t('collection.detailOnly')}
                        </Pill>
                      </div>
                      {field.description && <p className="field-defs__description">{field.description}</p>}
                      {field.options && field.options.length > 0 && (
                        <p className="field-defs__description">
                          {t('collection.options')} {field.options.map((option) => fieldOptionLabel(field, option, uiLang(i18n))).join(', ')}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </Dialog>
  );
}

/** The collection's name and description (the owner's). onSave resolves when saved. */
export function CollectionDetailsDialog({ inventory, onSave, onClose }) {
  const { t } = useTranslation();
  const [name, setName] = useState(inventory.name);
  const [description, setDescription] = useState(inventory.description || '');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({ name: name.trim(), description: description.trim() });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('collection.editTitle')}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="collection-details" disabled={!name.trim()} loading={saving}>{t('common.save')}</Button>
        </>
      )}
    >
      <form id="collection-details" className="dialog-form" onSubmit={submit}>
        <TextField label={t('collection.nameLabel')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Textarea label={t('collection.descriptionLabel')} value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('collection.descriptionPlaceholder')} rows={3} />
      </form>
    </Dialog>
  );
}
